import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Check,
    ChevronDown,
    Copy,
    Crop,
    Download,
    Image as ImageIcon,
    Layers,
    LayoutTemplate,
    Loader2,
    Maximize,
    Minus,
    Palette,
    Plus,
    Redo2,
    Shapes,
    Trash2,
    Type as TypeIcon,
    Undo2,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { useContentStudioApi } from '../../../../../common/hooks/use-content-studio-api.hook';
import type { CsBrandKit } from '../../../../../common/models/content-studio.model';
import type { VendorState } from '../../../../../redux/vendor/rootReducer';
import { defaultLogo } from '../../../../../theme';
import { ARTBOARD_PRESETS, artboardFromImage, ratioLabel } from './artboard';
import { resolveBrand, swatchesFor } from './colors';
import { ContextBar } from './ContextBar';
import { isImageObject } from './fabric-setup';
import { DEFAULT_PAIR, FONT_PAIRS } from './fonts';
import { Inspector } from './Inspector';
import { loadImageElement } from './objects';
import { BrandPanel } from './panels/BrandPanel';
import { ElementsPanel } from './panels/ElementsPanel';
import { ImagesPanel, PanelImage } from './panels/ImagesPanel';
import { LayersPanel } from './panels/LayersPanel';
import { TemplatesPanel } from './panels/TemplatesPanel';
import { TextPanel } from './panels/TextPanel';
import { ShortcutsPopover, useEditorShortcuts } from './shortcuts';
import { TEMPLATE_IDS } from './templates';
import type { DesignEditorModalProps, FontPair, RailTab, Translate } from './types';
import { useDesignEditor } from './useDesignEditor';
import { BarButton } from './ui';

export type { DesignEditorSaveResult, DesignEditorModalProps } from './types';

/** The brand kit changes rarely; one read per page is plenty. */
let kitCache: CsBrandKit | null | undefined;

const RAIL: Array<{ id: RailTab; icon: React.ReactNode; key: string }> = [
    { id: 'templates', icon: <LayoutTemplate />, key: 'DESIGN_EDITOR.TAB_TEMPLATES' },
    { id: 'text', icon: <TypeIcon />, key: 'DESIGN_EDITOR.TAB_TEXT' },
    { id: 'elements', icon: <Shapes />, key: 'DESIGN_EDITOR.TAB_ELEMENTS' },
    { id: 'brand', icon: <Palette />, key: 'DESIGN_EDITOR.TAB_BRAND' },
    { id: 'images', icon: <ImageIcon />, key: 'DESIGN_EDITOR.TAB_IMAGES' },
    { id: 'layers', icon: <Layers />, key: 'DESIGN_EDITOR.TAB_LAYERS' },
];

/**
 * The Design editor — a Canva-style layout tool for social graphics.
 *
 * Same props and save result as before, so both workspace mounts (design nodes
 * and the edit-image Design tool) are untouched: Save hands back the scene
 * JSON plus a PNG of the artboard at its native size.
 */
export const DesignEditorModal: React.FC<DesignEditorModalProps> = ({
    open,
    backgroundUrl,
    initialFabricJson,
    initialImageLayerUrls,
    brandKit,
    onClose,
    onSave,
}) => {
    const { t } = useTranslation();
    const tr: Translate = useCallback((k, o) => String(t(k, o as any)), [t]);
    const api = useContentStudioApi();
    const apiRef = useRef(api);
    apiRef.current = api;
    const { projectUuid } = useParams();
    const theme = useSelector((s: VendorState) => (s as any)?.settings?.theme);
    const accountLogo = theme?.logo && theme.logo !== defaultLogo ? theme.logo : undefined;
    const accountName = theme?.vendorName && theme.vendorName !== 'ArDealy' ? theme.vendorName : undefined;

    // ── Brand ──
    const [kit, setKit] = useState<CsBrandKit | null | undefined>(kitCache);
    useEffect(() => {
        if (!open || kitCache !== undefined) return;
        let cancelled = false;
        apiRef.current.getBrandKit()
            .then((k) => { kitCache = k || null; if (!cancelled) setKit(kitCache); })
            .catch(() => { kitCache = null; if (!cancelled) setKit(null); });
        return () => { cancelled = true; };
    }, [open]);
    const brand = useMemo(() => {
        const b = resolveBrand(kit, brandKit, accountLogo);
        if (!b.name && accountName) b.name = accountName;
        return b;
    }, [kit, brandKit, accountLogo, accountName]);
    const swatches = useMemo(() => swatchesFor(brand), [brand]);
    const [pair, setPair] = useState<FontPair>(DEFAULT_PAIR);
    const [useBrandFont, setUseBrandFont] = useState(true);

    // ── Project pictures + uploads ──
    const [projectImages, setProjectImages] = useState<PanelImage[]>([]);
    const [loadingProject, setLoadingProject] = useState(false);
    const [uploads, setUploads] = useState<PanelImage[]>([]);
    useEffect(() => {
        if (!open || !projectUuid) return;
        let cancelled = false;
        setLoadingProject(true);
        apiRef.current.listAssets({ projectUuid, limit: 200 })
            .then((res) => {
                if (cancelled) return;
                const items = (res?.items || [])
                    .filter((a) => a.status === 'ready' && a.url && (a.kind === 'image' || a.kind === 'upload') && !/\.(mp4|webm|mov)(\?|$)/i.test(a.url || ''))
                    .map((a) => ({ url: a.url as string, thumb: a.thumbnailUrl || (a.url as string) }));
                const seen = new Set<string>();
                setProjectImages(items.filter((i) => (seen.has(i.url) ? false : (seen.add(i.url), true))));
            })
            .catch(() => { if (!cancelled) setProjectImages([]); })
            .finally(() => { if (!cancelled) setLoadingProject(false); });
        return () => { cancelled = true; };
    }, [open, projectUuid]);

    const onUpload = useCallback(async (file: File): Promise<string | null> => {
        try {
            const res = await apiRef.current.uploadStyleRef(file);
            if (!res?.url) return null;
            setUploads((u) => [{ url: res.url, thumb: res.url }, ...u]);
            return res.url;
        } catch {
            return null;
        }
    }, []);

    // ── Canvas ──
    const [canvasEl, setCanvasEl] = useState<HTMLCanvasElement | null>(null);
    const [paneEl, setPaneEl] = useState<HTMLDivElement | null>(null);
    const projectUrls = useMemo(() => projectImages.map((p) => p.url), [projectImages]);
    const ed = useDesignEditor({
        open,
        canvasEl,
        paneEl,
        backgroundUrl,
        initialFabricJson,
        initialImageLayerUrls,
        brand,
        pair,
        projectImages: projectUrls,
        t: tr,
    });
    const edRef = useRef(ed);
    edRef.current = ed;

    const [tab, setTab] = useState<RailTab>(initialFabricJson ? 'text' : 'templates');
    useEffect(() => { if (open) setTab(initialFabricJson ? 'text' : 'templates'); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const [helpOpen, setHelpOpen] = useState(false);
    const openHelp = useCallback(() => setHelpOpen(true), []);
    useEditorShortcuts(ed, open, openHelp);

    // The picture's own proportions, offered as the "Original" size.
    const [originalSize, setOriginalSize] = useState<{ w: number; h: number } | null>(null);
    useEffect(() => {
        if (!open || !backgroundUrl) { setOriginalSize(null); return; }
        let cancelled = false;
        loadImageElement(backgroundUrl)
            .then((el) => { if (!cancelled) setOriginalSize(artboardFromImage(el.naturalWidth, el.naturalHeight)); })
            .catch(() => { /* no original size to offer */ });
        return () => { cancelled = true; };
    }, [open, backgroundUrl]);

    // ── Dev hooks for automated screenshots (never in production) ──
    useEffect(() => {
        if (!open || process.env.NODE_ENV === 'production') return undefined;
        const w = window as any;
        w.__csDesignApplyTemplate = async (id: string, pairId?: string) => {
            const p = FONT_PAIRS.find((x) => x.id === pairId);
            if (p) setPair(p);
            await edRef.current.applyTemplate(id, p ? { pair: p } : undefined);
            return TEMPLATE_IDS.includes(id);
        };
        w.__csDesignTemplates = () => [...TEMPLATE_IDS];
        w.__csDesignSetArtboard = (size: string | { w: number; h: number }) => {
            const preset = typeof size === 'string' ? ARTBOARD_PRESETS.find((p) => p.id === size) : null;
            const s = preset ? { w: preset.w, h: preset.h } : (typeof size === 'object' ? size : null);
            if (s) edRef.current.setArtboard(s);
            return s;
        };
        // Select the first layer of a kind ('textbox', 'image', 'rect'…) so a
        // screenshot can show the inspector for it.
        w.__csDesignSelect = (type: string, nth = 0) => {
            const objs = (edRef.current.canvas?.getObjects() || []).filter((o) => o.type === type && !(o as any).csLocked);
            const o = objs[nth] || null;
            edRef.current.select(o);
            return !!o;
        };
        w.__csDesignTab = (tabId: RailTab) => setTab(tabId);
        // Where a layer is on screen, so a test can drag or double-click it.
        w.__csDesignObjectRect = (type: string, nth = 0) => {
            const c = edRef.current.canvas;
            const o = (c?.getObjects() || []).filter((x) => x.type === type)[nth];
            const el = (c as any)?.upperCanvasEl as HTMLCanvasElement | undefined;
            if (!o || !el) return null;
            const r = o.getBoundingRect();
            const box = el.getBoundingClientRect();
            return { x: box.left + r.left, y: box.top + r.top, w: r.width, h: r.height };
        };
        w.__csDesignState = () => ({
            ready: edRef.current.ready,
            artboard: edRef.current.artboard,
            layers: edRef.current.canvas?.getObjects().length || 0,
        });
        // The exact picture Save would upload, as a data URL, plus the scene —
        // lets a test judge the output without writing to the project.
        w.__csDesignExport = async (type: 'image/png' | 'image/jpeg' = 'image/png') => {
            const blob = await edRef.current.exportBlob(type);
            const dataUrl = await new Promise<string>((resolve) => {
                const r = new FileReader();
                r.onload = () => resolve(String(r.result));
                r.readAsDataURL(blob);
            });
            return { bytes: blob.size, artboard: edRef.current.artboard, scene: edRef.current.sceneJSON(), dataUrl };
        };
        return () => {
            delete w.__csDesignApplyTemplate;
            delete w.__csDesignTemplates;
            delete w.__csDesignSetArtboard;
            delete w.__csDesignState;
            delete w.__csDesignExport;
            delete w.__csDesignSelect;
            delete w.__csDesignTab;
            delete w.__csDesignObjectRect;
        };
    }, [open]);

    // ── Save / download ──
    const [saving, setSaving] = useState(false);
    const handleSave = useCallback(async () => {
        if (saving || !ed.ready) return;
        setSaving(true);
        try {
            const pngBlob = await ed.exportBlob('image/png');
            const fabricJson = ed.sceneJSON();
            onSave({ fabricJson, pngBlob, width: ed.artboard.w, height: ed.artboard.h });
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[DesignEditor] save failed', err);
        } finally {
            setSaving(false);
        }
    }, [saving, ed, onSave]);

    const download = useCallback(async (type: 'image/png' | 'image/jpeg') => {
        try {
            const blob = await ed.exportBlob(type, 0.92);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `design-${ed.artboard.w}x${ed.artboard.h}.${type === 'image/png' ? 'png' : 'jpg'}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 4000);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[DesignEditor] download failed', err);
        }
    }, [ed]);

    // ── Floating selection toolbar position ──
    const sel = ed.active;
    let floatPos: { left: number; top: number } | null = null;
    if (sel && ed.ready && !ed.transforming && !ed.cropping && !(sel as any).isEditing && paneEl) {
        const r = sel.getBoundingRect();
        const above = r.top - 48;
        floatPos = { left: r.left + r.width / 2, top: above < 8 ? r.top + r.height + 12 : above };
    }

    const artLabel = `${ed.artboard.w} × ${ed.artboard.h}`;
    const preset = ARTBOARD_PRESETS.find((p) => p.w === ed.artboard.w && p.h === ed.artboard.h);
    const primaryImage = backgroundUrl || projectUrls[0] || null;
    const isOriginal = !!originalSize && originalSize.w === ed.artboard.w && originalSize.h === ed.artboard.h;

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
            <DialogContent
                showCloseButton={false}
                onEscapeKeyDown={(e) => e.preventDefault()}
                onOpenAutoFocus={(e) => e.preventDefault()}
                // A stray click or Escape must never throw away a design: the
                // editor closes only through Cancel or Save.
                onInteractOutside={(e) => e.preventDefault()}
                className="flex h-[94vh] w-[98vw] max-w-[1760px] flex-col gap-0 overflow-hidden bg-background p-0 sm:max-w-[1760px]"
            >
                {/* ── Header ── */}
                <div className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
                    <DialogTitle className="text-sm font-medium tracking-tight text-foreground">{t('DESIGN_EDITOR.TITLE')}</DialogTitle>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                className="ms-2 flex h-8 cursor-pointer items-center gap-2 rounded-lg border px-2.5 text-xs text-foreground transition-colors hover:bg-accent"
                            >
                                <span>{preset ? t(preset.labelKey) : isOriginal ? t('DESIGN_EDITOR.SIZE_ORIGINAL') : t('DESIGN_EDITOR.SIZE_CUSTOM')}</span>
                                <span className="tabular-nums text-muted-foreground">{artLabel}</span>
                                <ChevronDown className="size-3.5 text-muted-foreground" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-64">
                            <DropdownMenuLabel>{t('DESIGN_EDITOR.RESIZE')}</DropdownMenuLabel>
                            {originalSize && (
                                <DropdownMenuItem onSelect={() => ed.setArtboard(originalSize)}>
                                    <span className="flex-1">{t('DESIGN_EDITOR.SIZE_ORIGINAL')}</span>
                                    <span className="text-xs tabular-nums text-muted-foreground">{ratioLabel(originalSize)}</span>
                                    {ed.artboard.w === originalSize.w && ed.artboard.h === originalSize.h && <Check className="size-3.5" />}
                                </DropdownMenuItem>
                            )}
                            {ARTBOARD_PRESETS.map((p) => (
                                <DropdownMenuItem key={p.id} onSelect={() => ed.setArtboard({ w: p.w, h: p.h })}>
                                    <span className="flex-1">{t(p.labelKey)}</span>
                                    <span className="text-xs tabular-nums text-muted-foreground">{p.ratio}</span>
                                    {preset?.id === p.id && <Check className="size-3.5" />}
                                </DropdownMenuItem>
                            ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onSelect={() => ed.select(null)}>{t('DESIGN_EDITOR.SIZE_CUSTOM_EDIT')}</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <div className="ms-2 flex items-center gap-0.5">
                        <BarButton title={t('DESIGN_EDITOR.UNDO')} onClick={ed.undo} disabled={!ed.canUndo || ed.cropping}><Undo2 /></BarButton>
                        <BarButton title={t('DESIGN_EDITOR.REDO')} onClick={ed.redo} disabled={!ed.canRedo || ed.cropping}><Redo2 /></BarButton>
                    </div>

                    <div className="flex-1" />

                    <ShortcutsPopover open={helpOpen} onOpenChange={setHelpOpen} />
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button type="button" variant="outline" size="lg" disabled={!ed.ready}>
                                <Download />
                                {t('DESIGN_EDITOR.DOWNLOAD')}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onSelect={() => { void download('image/png'); }}>
                                <span className="flex-1">PNG</span>
                                <span className="text-xs text-muted-foreground">{t('DESIGN_EDITOR.DOWNLOAD_PNG_HINT')}</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => { void download('image/jpeg'); }}>
                                <span className="flex-1">JPG</span>
                                <span className="text-xs text-muted-foreground">{t('DESIGN_EDITOR.DOWNLOAD_JPG_HINT')}</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <Button type="button" variant="ghost" size="lg" onClick={onClose}>{t('DESIGN_EDITOR.CANCEL')}</Button>
                    <Button type="button" size="lg" onClick={() => { void handleSave(); }} disabled={saving || !ed.ready} className="min-w-28">
                        {saving && <Loader2 className="size-3.5 animate-spin" />}
                        {t('DESIGN_EDITOR.SAVE')}
                    </Button>
                </div>

                {/* ── Body ── */}
                <div className="flex min-h-0 flex-1">
                    {/* Rail */}
                    <nav className="flex w-[72px] shrink-0 flex-col items-center gap-1 border-e py-3">
                        {RAIL.map((r) => (
                            <button
                                key={r.id}
                                type="button"
                                onClick={() => setTab(r.id)}
                                aria-pressed={tab === r.id}
                                className={cn(
                                    'flex w-[60px] cursor-pointer flex-col items-center gap-1 rounded-xl py-2 text-[10.5px] transition-colors [&_svg]:size-[19px]',
                                    tab === r.id ? 'bg-foreground/[0.07] font-medium text-foreground' : 'text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground',
                                )}
                            >
                                {r.icon}
                                <span>{t(r.key)}</span>
                            </button>
                        ))}
                    </nav>

                    {/* Panel */}
                    <div className="w-[300px] shrink-0 overflow-y-auto border-e px-4 py-4">
                        {ed.ready ? (
                            <>
                                {tab === 'templates' && (
                                    <TemplatesPanel ed={ed} brand={brand} pair={pair} onPairChange={setPair} useBrandFont={useBrandFont} onUseBrandFontChange={setUseBrandFont} tr={tr} />
                                )}
                                {tab === 'text' && <TextPanel ed={ed} brand={brand} pair={pair} />}
                                {tab === 'elements' && <ElementsPanel ed={ed} primaryImage={primaryImage} />}
                                {tab === 'brand' && <BrandPanel ed={ed} brand={brand} hasKit={!!kit} />}
                                {tab === 'images' && (
                                    <ImagesPanel ed={ed} projectImages={projectImages} uploads={uploads} loadingProject={loadingProject} onUpload={onUpload} />
                                )}
                                {tab === 'layers' && <LayersPanel ed={ed} />}
                            </>
                        ) : (
                            <div className="flex justify-center py-10"><Loader2 className="size-4 animate-spin text-muted-foreground" /></div>
                        )}
                    </div>

                    {/* Centre */}
                    <div className="flex min-w-0 flex-1 flex-col">
                        <ContextBar ed={ed} brand={brand} swatches={swatches} onOpenImages={() => setTab('images')} />
                        <div
                            ref={setPaneEl}
                            className={cn(
                                'relative min-h-0 flex-1 overflow-hidden bg-muted bg-[radial-gradient(circle,var(--border)_1px,transparent_1px)] bg-[length:20px_20px]',
                                ed.spaceDown && 'cursor-grab',
                            )}
                        >
                            {/* The artboard's hairline + lift, drawn behind the canvas. */}
                            {ed.ready && (
                                <div
                                    className="pointer-events-none absolute rounded-[2px] shadow-[0_1px_2px_rgba(0,0,0,0.06),0_8px_28px_rgba(0,0,0,0.08)] ring-1 ring-foreground/10"
                                    style={{
                                        left: ed.view.tx,
                                        top: ed.view.ty,
                                        width: ed.artboard.w * ed.view.zoom,
                                        height: ed.artboard.h * ed.view.zoom,
                                    }}
                                />
                            )}
                            <div className="absolute inset-0">
                                <canvas ref={setCanvasEl} />
                            </div>
                            {!ed.ready && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <Loader2 className="size-5 animate-spin text-muted-foreground" />
                                </div>
                            )}

                            {floatPos && (
                                <div
                                    className="absolute z-10 flex -translate-x-1/2 items-center gap-0.5 rounded-xl border bg-background p-1 shadow-[0_4px_16px_rgba(0,0,0,0.08)]"
                                    style={{ left: floatPos.left, top: floatPos.top }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    {sel && isImageObject(sel) && !(sel as any).csLocked && (
                                        <BarButton title={t('DESIGN_EDITOR.CROP')} onClick={ed.startCrop}><Crop /></BarButton>
                                    )}
                                    <BarButton title={t('DESIGN_EDITOR.DUPLICATE')} onClick={ed.duplicate}><Copy /></BarButton>
                                    <BarButton title={t('DESIGN_EDITOR.DELETE')} onClick={ed.deleteSelection} destructive><Trash2 /></BarButton>
                                </div>
                            )}

                            {/* Zoom */}
                            <div className="absolute end-4 bottom-4 z-[5] flex items-center gap-0.5 rounded-xl border bg-background p-1 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
                                <BarButton title={t('DESIGN_EDITOR.ZOOM_OUT')} onClick={() => ed.zoomBy(1 / 1.2)}><Minus /></BarButton>
                                <button
                                    type="button"
                                    onClick={() => ed.zoomTo(1)}
                                    title={t('DESIGN_EDITOR.ZOOM_100')}
                                    className="h-8 min-w-14 cursor-pointer rounded-lg px-1.5 text-xs tabular-nums text-foreground hover:bg-foreground/[0.06]"
                                >
                                    {Math.round(ed.view.zoom * 100)}%
                                </button>
                                <BarButton title={t('DESIGN_EDITOR.ZOOM_IN')} onClick={() => ed.zoomBy(1.2)}><Plus /></BarButton>
                                <BarButton title={t('DESIGN_EDITOR.ZOOM_FIT')} onClick={ed.zoomFit}><Maximize /></BarButton>
                            </div>
                        </div>
                    </div>

                    {/* Inspector */}
                    <aside className="w-[288px] shrink-0 overflow-y-auto border-s">
                        <Inspector ed={ed} brand={brand} swatches={swatches} onOpenImages={() => setTab('images')} originalSize={originalSize} />
                    </aside>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default DesignEditorModal;
